import { type ChangeEvent, useCallback, useState } from 'react';
import { getClasses } from '@pasqal/core/helpers/styles';
import { clamp, getSuitableDecimalDigits } from '~/util';
import './NumberInput.css';

interface NumberInputProps {
  name?: string;
  value: number;
  onChange: (val: number) => void;
  max: number;
  min: number;
  step?: number;
  disabled?: boolean;
  unit?: string;
  onBlur?: () => void;
  onFocus?: () => void;
  className?: string;
}

export default function NumberInput({
  name,
  value,
  onChange,
  max,
  min,
  step,
  disabled = false,
  unit,
  onBlur,
  onFocus,
  className
}: NumberInputProps) {
  const formatValue = useCallback(
    (v: number) => {
      const p = getSuitableDecimalDigits(v, step);
      return parseFloat(v.toFixed(p)).toString();
    },
    [step]
  );

  // Non-null only while the <input> is focused — tracks what the user is typing.
  // When null the component is fully controlled by the `value` prop.
  const [userInput, setUserInput] = useState<string | null>(null);

  const emitValue = (raw: number) => {
    let newValue = clamp(raw, min, max);
    if (step != null) {
      newValue = Math.round((newValue - min) / step) * step + min;
    }
    onChange(newValue);
  };

  const handleFocus = () => {
    setUserInput(formatValue(value));
    onFocus?.();
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setUserInput(text);
    const parsed = parseFloat(text);
    if (!isNaN(parsed) && parsed >= min && parsed <= max) {
      emitValue(parsed);
    }
  };

  const handleBlur = () => {
    const text = userInput ?? formatValue(value);
    const parsed = parseFloat(text);
    if (!isNaN(parsed)) {
      emitValue(clamp(parsed, min, max));
    }
    setUserInput(null);
    onBlur?.();
  };

  // While focused: show whatever the user has typed.
  // While not focused: always reflect the prop value — reacts to every parent update.
  const displayValue = userInput ?? formatValue(value);

  const css = getClasses(['NumberInput', className]);

  return (
    <div className={css}>
      <input
        id={name}
        value={displayValue}
        min={min}
        max={max}
        step={step}
        type="number"
        disabled={disabled}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleChange}
      />
      {unit && <span className="NumberInput-unit">{unit}</span>}
    </div>
  );
}
